/* Blog grid — splash transition, fade-in tiles, fetch-on-scroll for
 * paginated pages, client-side tag filtering, URL sync.
 *
 * SPLASH
 * On first paint the .blog-grid__splash covers the visible viewport
 * with the Magazine title + subtitle. After data-splash-delay
 * milliseconds (default 500), this script adds .blog-grid--ready to
 * the root section. CSS transitions then fade the splash out, slide
 * the grid list up into place, and fade the sticky Magazine/pill
 * overlay in. In Shopify.designMode the timer is skipped so merchants
 * configure section settings against the final state.
 *
 * LAYOUT MODEL
 * The list is always rebuilt from a single source of truth: the
 * `articleData` array of slot elements in canonical order. On every
 * filter change or background-page fetch, we wipe `.blog-grid__list`
 * and re-emit groups + slots by assigning A/B + big/small-top/
 * small-bottom position classes based on each slot's *new* index in
 * whatever subset we're rendering. This is what lets filtered views
 * never have holes — survivors flow into the canonical magazine
 * pattern positions instead of staying in their original spots.
 *
 * FADE-IN ON ENTER VIEWPORT
 * Each slot starts at opacity 0 (CSS) and gets the `--in-view` class
 * added the first time an IntersectionObserver sees it intersect the
 * viewport. The class triggers a CSS transition to opacity 1. Once
 * added, the class never comes off — slots that scroll back out of
 * viewport stay visible. No progressive-reveal hiding; every slot in
 * articleData is rendered into the DOM at full layout space, just
 * invisible until first scroll-into-view.
 *
 * FETCH-ON-SCROLL
 * A 1px sentinel pinned at the bottom of the list is watched by its
 * own IntersectionObserver. When the visitor scrolls to the bottom
 * AND more paginated pages remain (data-total-pages > current), the
 * next page fetches via Shopify's Section Rendering API:
 *   /blogs/<handle>?section_id=<section-id>&page=<n>
 * The response is parsed, .blog-grid__slot elements are appended to
 * articleData, the layout rebuilds. New slots start opacity 0 and
 * fade in as they enter the viewport just like the original 50.
 *
 * CLIENT-SIDE TAG FILTERING (multi-select, OR semantics)
 * Pills carry data-filter-tag (handleized). Slots carry data-tags
 * (pipe-joined handleized native article tags). Clicking a pill
 * toggles that tag in the active Set — multiple pills can be active
 * simultaneously and the filter shows a slot if it matches any active
 * tag (union). Clicking an active pill removes just that tag;
 * clicking the last active pill clears the filter entirely.
 *
 * URL sync uses `?tag=foo+bar` (`+` separator), mirroring Shopify's
 * native /blogs/<handle>/tagged/foo+bar convention. On initial load
 * the URL's ?tag= param is split on `+` and each matching pill is
 * activated. NOTE: filter only sees articles currently in the DOM —
 * articles on not-yet-fetched pages are invisible to it. */

(function () {
  const grid = document.querySelector('.blog-grid');
  if (!grid) return;
  const listEl = grid.querySelector('.blog-grid__list');
  if (!listEl) return;

  // -------- splash → ready --------

  const delay = parseInt(grid.dataset.splashDelay, 10) || 500;
  // Honor prefers-reduced-motion — motion-sensitive visitors skip the
  // splash-fade timer entirely so the grid is usable immediately.
  const reduceMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (
    reduceMotion ||
    (window.Shopify && window.Shopify.designMode)
  ) {
    grid.classList.add('blog-grid--ready');
  } else {
    requestAnimationFrame(() => {
      setTimeout(() => {
        grid.classList.add('blog-grid--ready');
      }, delay);
    });
  }

  // -------- state --------

  const ARTICLES_PER_GROUP = 3;
  const sectionId = grid.dataset.sectionId || '';
  const totalPages = parseInt(listEl.dataset.totalPages, 10) || 1;
  let currentPage = parseInt(listEl.dataset.currentPage, 10) || 1;
  let isFetching = false;

  // Canonical source of truth — slot elements in their original
  // server-rendered order. Layout is always rebuilt from this list
  // (or a filtered subset), so positions reflow correctly when slots
  // are hidden.
  let articleData = Array.from(grid.querySelectorAll('.blog-grid__slot'));
  let groups = Array.from(grid.querySelectorAll('.blog-grid__group'));
  const pills = Array.from(grid.querySelectorAll('.blog-grid__pill'));

  // Classes a slot can carry to express its position within the
  // magazine pattern. Cleared and re-applied on every render.
  const POSITION_CLASSES = [
    'blog-grid__slot--big',
    'blog-grid__slot--small',
    'blog-grid__slot--small-top',
    'blog-grid__slot--small-bottom',
  ];

  // Active filter tags. Declared up here (above renderLayout /
  // rebuildAndReveal which read it) so the initial rebuild on script
  // start doesn't hit a let temporal-dead-zone reference error.
  let activeTags = new Set();

  // Sentinel — a 1px invisible div pinned at the bottom of the list.
  // One IntersectionObserver watches it for the fetch-next-page cycle.
  const sentinel = document.createElement('div');
  sentinel.className = 'blog-grid__sentinel';
  sentinel.setAttribute('aria-hidden', 'true');

  let isHandlingSentinel = false;
  const SENTINEL_MARGIN = 400;
  let sentinelObserver = null;

  // Per-slot fade-in observer. One instance watches every slot. When
  // a slot enters the viewport, it gets the `--in-view` class (CSS
  // transitions opacity from 0 to 1) and is unobserved — one-shot,
  // never fades back out. New slots from fetched pages get observed
  // after each layout rebuild.
  const FADE_MARGIN = 100;
  let slotFadeObserver = null;

  function initSlotFadeObserver() {
    if (slotFadeObserver) return;
    if (typeof IntersectionObserver === 'undefined') {
      // Old browser fallback: skip the fade entirely, mark every slot
      // as in-view so they show without animation.
      articleData.forEach((s) => s.classList.add('blog-grid__slot--in-view'));
      return;
    }
    slotFadeObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('blog-grid__slot--in-view');
          slotFadeObserver.unobserve(entry.target);
        });
      },
      { rootMargin: FADE_MARGIN + 'px 0px' }
    );
  }

  function observeSlotForFade(slot) {
    if (!slotFadeObserver) return;
    // If a slot already faded in, don't re-observe — its --in-view
    // class is sticky regardless of layout shuffles.
    if (slot.classList.contains('blog-grid__slot--in-view')) return;
    slotFadeObserver.observe(slot);
  }

  // -------- layout build --------

  function renderLayout(slotList) {
    // Wipe and rebuild the list contents. Each slot is re-positioned
    // within a fresh group structure: positions cycle [big, small-top,
    // small-bottom] per group; groups alternate A/B every 3 slots.
    // The result is the magazine pattern always reading gap-free over
    // whatever subset we hand it.
    listEl.textContent = '';
    let currentGroup = null;
    slotList.forEach((slot, idx) => {
      const posInGroup = idx % ARTICLES_PER_GROUP;
      const groupIdx = Math.floor(idx / ARTICLES_PER_GROUP);
      const isA = groupIdx % 2 === 0;

      if (posInGroup === 0) {
        currentGroup = document.createElement('div');
        currentGroup.className =
          'blog-grid__group blog-grid__group--' + (isA ? 'a' : 'b');
        listEl.appendChild(currentGroup);
      }

      // Clean position + leftover state classes, then re-apply based
      // on slot's new index in this render. Keep --in-view if already
      // set (so previously-faded slots stay visible after a filter
      // rebuild moves them between groups).
      POSITION_CLASSES.forEach((c) => slot.classList.remove(c));
      slot.classList.remove('blog-grid__slot--filter-hidden');
      if (posInGroup === 0) {
        slot.classList.add('blog-grid__slot--big');
      } else {
        slot.classList.add('blog-grid__slot--small');
        slot.classList.add(
          posInGroup === 1
            ? 'blog-grid__slot--small-top'
            : 'blog-grid__slot--small-bottom'
        );
      }

      currentGroup.appendChild(slot);
      // Observe slots that haven't faded in yet so they fade when
      // their new position scrolls into the viewport.
      observeSlotForFade(slot);
    });

    // Sentinel must always be the last child of listEl so the
    // fetch-page observer fires when the visitor scrolls past the
    // actual end of the list. listEl.textContent = '' wiped it out at
    // the top of this function, so re-append here.
    listEl.appendChild(sentinel);

    // Refresh the groups cache against the newly-built DOM.
    groups = Array.from(grid.querySelectorAll('.blog-grid__group'));
  }

  // -------- sentinel observer (fetch next page only) --------

  function isSentinelInViewport() {
    // Mirror the IntersectionObserver's rootMargin so the cascade
    // loop in handleSentinel terminates at the same threshold the
    // observer fires at. Without this the loop could break early and
    // wait for another scroll, even though the sentinel is still
    // within trigger range.
    const rect = sentinel.getBoundingClientRect();
    return (
      rect.top - SENTINEL_MARGIN < window.innerHeight &&
      rect.bottom + SENTINEL_MARGIN > 0
    );
  }

  async function handleSentinel() {
    // Cascade loop: fetch the next paginated page while the sentinel
    // stays within trigger range. Without progressive reveal, the
    // only action is fetching — the layout simply grows downward as
    // new slots are appended and fade in.
    //
    // Handles the sparse-filtered case: if the filter matches few or
    // none of the new slots, the sentinel may remain in viewport
    // (because the visible list barely grew). Loop fires another
    // fetch until matches push the sentinel out of range or all
    // pages are exhausted.
    if (isHandlingSentinel) return;
    isHandlingSentinel = true;
    try {
      while (currentPage < totalPages && !isFetching) {
        await fetchNextPage();
        if (!isSentinelInViewport()) break;
      }
    } finally {
      isHandlingSentinel = false;
    }
  }

  function startSentinelObserver() {
    if (sentinelObserver) sentinelObserver.disconnect();
    sentinelObserver = null;

    if (typeof IntersectionObserver === 'undefined') return;

    sentinelObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            handleSentinel();
            break;
          }
        }
      },
      { rootMargin: SENTINEL_MARGIN + 'px 0px' }
    );
    sentinelObserver.observe(sentinel);
  }

  async function fetchNextPage() {
    if (isFetching) return;
    if (currentPage >= totalPages) return;
    isFetching = true;
    try {
      const nextPage = currentPage + 1;
      const url = new URL(window.location.href);
      // Section Rendering API: returns just this section's HTML.
      // `page` is forwarded into the section's {% paginate %} block.
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('page', String(nextPage));
      // Drop any active ?tag= from the URL we fetch — the section
      // doesn't filter server-side (filtering is client-side), and
      // leaving the param in is just noise.
      url.searchParams.delete('tag');

      const res = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!res.ok) {
        console.warn('[blog-grid] page ' + nextPage + ' returned ' + res.status);
        currentPage = nextPage;
        return;
      }
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newSlots = Array.from(doc.querySelectorAll('.blog-grid__slot'));
      currentPage = nextPage;
      if (newSlots.length === 0) return;

      // Append new slots to the canonical list, then rebuild the
      // layout so they slot into the magazine pattern. The sentinel
      // gets re-appended as the last child by renderLayout.
      newSlots.forEach((s) => articleData.push(s));
      const view = getViewSlots();
      renderLayout(view);
    } catch (e) {
      console.warn('[blog-grid] page fetch failed', e);
    } finally {
      isFetching = false;
    }
  }

  // -------- filter + render orchestration --------

  function getViewSlots() {
    if (activeTags.size === 0) return articleData;
    const activeArr = [...activeTags];
    return articleData.filter((s) => {
      const slotTags = (s.dataset.tags || '').split('|').filter(Boolean);
      return activeArr.some((t) => slotTags.indexOf(t) !== -1);
    });
  }

  function rebuild() {
    const view = getViewSlots();
    renderLayout(view);
    startSentinelObserver();
  }

  // -------- initial paint --------

  initSlotFadeObserver();
  rebuild();

  // -------- client-side tag filter (multi-select, OR) --------
  // `activeTags` is declared higher up so the initial rebuild can
  // read it. Set semantics: trivial add/delete/has, and iteration
  // order is insertion order which we lean on for stable URL strings.

  // Accepts: string ("foo+bar"), array, Set, or empty.
  // Splits strings on `+` OR whitespace because `?tag=foo+bar` typed
  // literally in the URL bar (without %2B encoding) gets decoded as
  // "foo bar" by URLSearchParams — that's the legacy x-www-form-urlencoded
  // rule. URLs generated by syncUrl below always encode `+` as %2B
  // correctly, so this split is only a safety net for hand-typed URLs.
  function normalizeTags(input) {
    if (!input) return [];
    if (typeof input === 'string') return input.split(/[+\s]+/).filter(Boolean);
    if (Array.isArray(input)) return input.filter(Boolean);
    if (input instanceof Set) return [...input].filter(Boolean);
    return [];
  }

  function applyFilter(input) {
    activeTags = new Set(normalizeTags(input));

    pills.forEach((p) => {
      const isActive = activeTags.has(p.dataset.filterTag);
      p.classList.toggle('blog-grid__pill--active', isActive);
      // aria-pressed is the correct attribute for toggle buttons.
      p.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    rebuild();
  }

  function syncUrl(tags, replace) {
    if (!window.history || typeof window.history.pushState !== 'function') return;
    const url = new URL(window.location.href);
    const arr = normalizeTags(tags);
    if (arr.length > 0) {
      // `+` separator mirrors Shopify's /blogs/<handle>/tagged/foo+bar
      // path convention. URLSearchParams encodes `+` as %2B in the
      // value, which is fine — both representations decode the same.
      url.searchParams.set('tag', arr.join('+'));
    } else {
      url.searchParams.delete('tag');
    }
    const method = replace ? 'replaceState' : 'pushState';
    window.history[method]({ tags: arr }, '', url.toString());
  }

  // Initial filter from ?tag=… so direct-link shareable URLs work.
  const initialTagParam = new URL(window.location.href).searchParams.get('tag') || '';
  if (initialTagParam) {
    applyFilter(initialTagParam);
  }

  // Scroll the section back into view after a filter change. Without
  // this, a visitor who clicked a pill while scrolled deep into the
  // list ends up parked over the footer when the filtered subset is
  // shorter than their scroll offset.
  function scrollToGridTop() {
    const rect = grid.getBoundingClientRect();
    const targetY = rect.top + window.scrollY;
    window.scrollTo({
      top: targetY,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  pills.forEach((pill) => {
    pill.addEventListener('click', () => {
      const tag = pill.dataset.filterTag || '';
      if (!tag) return;
      // Toggle this tag in the active set — multi-select.
      const next = new Set(activeTags);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
      }
      applyFilter(next);
      syncUrl(next, false);
      scrollToGridTop();
    });
  });

  // Back/forward — re-read the URL and re-apply the filter.
  window.addEventListener('popstate', () => {
    const tagParam = new URL(window.location.href).searchParams.get('tag') || '';
    applyFilter(tagParam);
  });
})();
